import type { TradingFeedResponse } from "../api/types";
import type { BiasAxis, PaneSide, TradingAlert } from "./types";

export function biasAxis(label: string | null | undefined): BiasAxis {
  const value = (label ?? "").toLowerCase();
  if (value.includes("neutral_to_bullish")) return "bull";
  if (value.includes("neutral_to_bearish")) return "bear";
  const hasBull = value.includes("bullish");
  const hasBear = value.includes("bearish");
  if (hasBull && !hasBear) return "bull";
  if (hasBear && !hasBull) return "bear";
  return "neutral";
}

export function deriveAlerts({
  pane,
  previous,
  next,
  now,
  highThreshold,
}: {
  pane: PaneSide;
  previous: TradingFeedResponse | null;
  next: TradingFeedResponse;
  now: number;
  highThreshold: number;
}): TradingAlert[] {
  const previousClusterIds = new Set(previous?.clusters.map((cluster) => cluster.cluster_id) ?? []);
  const alerts: TradingAlert[] = [];

  for (const cluster of next.clusters) {
    const score = cluster.impact?.relevance_score ?? 0;
    if (!previousClusterIds.has(cluster.cluster_id) && score >= highThreshold) {
      alerts.push({
        id: `${pane}:${next.material_id}:high:${cluster.cluster_id}`,
        pane,
        materialId: next.material_id,
        kind: "high-cluster",
        title: cluster.representative_title,
        createdAt: now,
        targetId: cluster.cluster_id,
        severity: "HIGH",
      });
    }
  }

  const previousAnalysis = previous?.current_analysis ?? null;
  const nextAnalysis = next.current_analysis;
  if (previousAnalysis?.id && nextAnalysis && nextAnalysis.id !== previousAnalysis.id) {
    const previousLabel = previousAnalysis.body?.directional_bias.label ?? null;
    const nextLabel = nextAnalysis.body?.directional_bias.label ?? null;
    if (!previousLabel || !nextLabel) return alerts;
    const before = biasAxis(previousLabel);
    const after = biasAxis(nextLabel);
    if (before !== after) {
      alerts.push({
        id: `${pane}:${next.material_id}:bias:${previousAnalysis.id}:${nextAnalysis.id}`,
        pane,
        materialId: next.material_id,
        kind: "bias-cross",
        title: `${previousLabel ?? "unknown"} -> ${nextLabel ?? "unknown"}`,
        createdAt: now,
        targetId: nextAnalysis.id,
        severity: "HIGH",
      });
    }
  }

  return alerts;
}

export class AlertEmitter extends EventTarget {
  emit(alert: TradingAlert): void {
    this.dispatchEvent(new CustomEvent<TradingAlert>("trading-alert", { detail: alert }));
  }
}

export const alertEmitter = new AlertEmitter();
